import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import {
    Card,
    CardContent,
    CardMedia,
    Grid,
    Stack,
    Typography,
    Chip,
    Box,
    CircularProgress,
    Button,
    useTheme
} from "@mui/material";
import { RootState } from "../../store";
import forOthersImage from "../../assets/images/about_others_banner.png";
import { Loading } from "../../components";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getUserDocuments, getAsset } from "../../services/apiService"; 
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslationContext } from "../../context/TranslationContext";
import { FormattedPhrase } from "../../context/FormattedPhrase";

interface Document {
    id: string;
    name: string;
    available: boolean;
    expirationDate?: string;
    aI_Key: string;
    latestVersionId: string;
}

export const MyDocuments: React.FC = () => {
    const { user } = useAuth0();
    const { loading } = useSelector((state: RootState) => state.user);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [fileLoading, setFileLoading] = useState<string>();
    const { getFormattedPhrase } = useTranslationContext();
    const theme = useTheme();

    const asyncLoadingTasks = async () => {
        if (user) {
            setIsLoading(true);
            try {
                let imKey = user["http://schemas.ccl.org/accounts/claims/ccl-identity/id"];
                let resp = await getUserDocuments(imKey);

                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const combinedDocuments = [
                    ...(resp.accessibleAssets || []),
                    ...(resp.ownedAssets || []),
                ]
                .filter(doc => {
                    if (!doc.expirationDate) return true;
                    const expDate = new Date(doc.expirationDate);
                    return expDate >= today;
                })
                .filter(doc => {
                    //the assetproperties distinguish Assessment Reports, from other files
                    if (!doc.assetProperties || doc.assetProperties.length === 0) return true;
                    
                    //the following filtering takes care of reports once released, but then withdrawn
                    if (!doc.assetVersions || !doc.latestVersionId) return false;
                    const latestVersion = doc.assetVersions.find(
                        version => version.versionID === doc.latestVersionId
                    );
                    return latestVersion && latestVersion.startDate !== null;
                })
                .map(doc => ({
                    id: doc.aI_Key,
                    name: doc.name,
                    available: doc.available,
                    expirationDate: doc.expirationDate,
                    aI_Key: doc.aI_Key,
                    latestVersionId: doc.latestVersionId
                }));
                setDocuments(combinedDocuments);
            } catch (error) {
                console.error("Error fetching documents:", error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleViewDocument = async (assetInfoId: string, versionId: string, fileName: string) => {
        if (!user || fileLoading) return;
        
        const individualId = user["http://schemas.ccl.org/accounts/claims/ccl-identity/id"];
        try {
            setFileLoading(assetInfoId);
            const response = await getAsset(parseInt(individualId), parseInt(assetInfoId), versionId);
            const data = new Blob([response], { type: "application/pdf" });
            const url = window.URL.createObjectURL(data);

            // Create a temporary anchor to trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Revoke after download trigger
            setTimeout(() => URL.revokeObjectURL(url), 100);
        } catch (error) {
            console.error("Error downloading document:", error);
        } finally {
            setFileLoading(undefined);
        }
    };

    useEffect(() => {
        sessionStorage.setItem("redirectUser", "");
        window.scrollTo(0, 0);
        asyncLoadingTasks();
    }, [user]);

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: getFormattedPhrase('myDocs.table.head1'),
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <PictureAsPdfIcon 
                        color={"error"} 
                        fontSize="small" 
                        sx={{ mr: 3, mt: 1, opacity: 1 }}
                    />
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <Typography>{params.value}</Typography>
                    </Box>
                </Box>
            )
        },
        {
            field: 'actions',
            headerName: '',
            width: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => (
                params.row.available ? (
                    <Button
                        variant="outlined"
                        onClick={() => handleViewDocument(params.row.aI_Key, params.row.latestVersionId, params.row.name)}
                        sx={{
                            fontWeight: 500,
                            borderRadius: "100px",
                            alignContent: "center",
                            height: 30,
                            fontSize: "0.938rem",
                            width: "100%"
                        }}
                        className="btn btn-outline-primary"
                    >
                        {
                            fileLoading == params.row.aI_Key ? <CircularProgress size={20} color={"primary"} /> :
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                }}
                            >
                                {getFormattedPhrase("myDocs.table.download")}
                            </Typography>
                        }
                    </Button>
                ) : null
            )
        }
    ];

    return (
        <Grid container>
            {!loading && (
                <>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ m: 3 }}>
                            <CardMedia
                                component="img"
                                image={forOthersImage}
                                alt="Survey for Others Card Image"
                                sx={{
                                    width: '100%',
                                    height: '60%',
                                    objectFit: 'cover',
                                }}
                            />
                            <CardContent>
                                <Stack>
                                    <Typography variant="title" sx={{ my: 3 }} fontWeight={700}>
                                        <FormattedPhrase id="myDocs.title" />
                                    </Typography>
                                    <Typography variant="body1">
                                        <FormattedPhrase id="myDocs.leftPane.text" />
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={8} style={{backgroundColor: "white", borderRadius: "12px", padding: 5, marginTop: 10}}>
                        <Fragment>
                            {isLoading ? (
                                <Loading />
                            ) : (
                                <Box sx={{ height: '70vh', width: '100%', p: 2 }}>
                                    <DataGrid
                                        rows={documents}
                                        columns={columns}
                                        disableRowSelectionOnClick
                                        sx={{
                                            borderRadius: "12px"
                                        }}
                                        slots={{
                                            noRowsOverlay: () => (
                                            <Stack height="100%" alignItems="center" justifyContent="center">
                                                <Typography variant="body1" color="text.secondary">
                                                    No documents available
                                                </Typography>
                                            </Stack>
                                            )
                                        }}
                                        hideFooter={true}
                                    />
                                </Box>
                            )}
                        </Fragment>
                    </Grid>
                </>
            )}
        </Grid>
    );
};
